<template>
  <main id="main" class="main dash-container">
    <div class="pagetitle"> 
      <h1 class="text-left">DASHBOARD</h1>
      <nav>
        <ol class="breadcrumb breadJa">
          <router-link :to="{ name: 'home' }" class="breadcrumb-item">Home</router-link>
        </ol>
      </nav>
    </div>

    <section class="section dashboard">
      
      <!-- Stats Cards - Glassmorphism -->
      <div class="dash-stats-grid">
        
        <!-- Total Instances -->
        <div class="dash-stat-card">
          <div class="dash-stat-icon">
            <i class="bi bi-phone-fill"></i>
          </div>
          <div class="dash-stat-content">
            <div class="dash-stat-label">Total Instances</div>
            <h2 class="dash-stat-number">{{ totalInstances }}</h2>
            <p class="dash-stat-meta">
              <span class="dash-stat-trend" v-if="onlineInstances > 0">
                <i class="bi bi-arrow-up"></i> {{ onlineInstances }} Online
              </span>
              <span v-else>{{ onlineInstances }} Online</span>
            </p>
          </div>
        </div>

        <!-- Active Rooms -->
        <div class="dash-stat-card">
          <div class="dash-stat-icon dash-success">
            <i class="bi bi-door-open-fill"></i>
          </div>
          <div class="dash-stat-content">
            <div class="dash-stat-label">Active Rooms</div>
            <h2 class="dash-stat-number">{{ activeRooms }}</h2>
            <p class="dash-stat-meta">{{ totalRooms }} Total Rooms</p>
          </div>
        </div>

        <!-- Total Scripts -->
        <div class="dash-stat-card">
          <div class="dash-stat-icon dash-warning">
            <i class="bi bi-file-text-fill"></i>
          </div>
          <div class="dash-stat-content">
            <div class="dash-stat-label">Total Scripts</div>
            <h2 class="dash-stat-number">{{ totalScripts }}</h2>
            <p class="dash-stat-meta">{{ totalTemplates }} Templates</p>
          </div>
        </div>

        <!-- Messages Today -->
        <div class="dash-stat-card">
          <div class="dash-stat-icon dash-info">
            <i class="bi bi-send-fill"></i>
          </div>
          <div class="dash-stat-content">
            <div class="dash-stat-label">Messages Today</div>
            <h2 class="dash-stat-number">{{ messagesToday }}</h2>
            <p class="dash-stat-meta">
              <span class="dash-stat-trend" v-if="successRate > 90">
                <i class="bi bi-check-circle"></i> {{ successRate }}% Success
              </span>
              <span v-else>{{ successRate }}% Success</span>
            </p>
          </div>
        </div>

      </div>

      <!-- Feature Cards -->
      <div class="dash-feature-grid mb-4">
        
        <!-- Instances Card -->
        <div class="dash-feature-card" @click="$router.push('/instances')">
          <div class="dash-feature-header">
            <h3 class="dash-feature-title">Instances</h3>
            <div class="dash-feature-icon">
              <i class="bi bi-phone"></i>
            </div>
          </div>
          <div class="dash-feature-content">
            <p class="dash-feature-description">
              Manage your WhatsApp instances. Create, monitor, and control all your connected devices.
            </p>
            <a href="#" class="dash-feature-action" @click.prevent>
              Manage Instances <i class="bi bi-arrow-right"></i>
            </a>
          </div>
        </div>

        <!-- Warming Rooms Card -->
        <div class="dash-feature-card" @click="$router.push('/warming/rooms')">
          <div class="dash-feature-header">
            <h3 class="dash-feature-title">Warming Rooms</h3>
            <div class="dash-feature-icon">
              <i class="bi bi-door-open"></i>
            </div>
          </div>
          <div class="dash-feature-content">
            <p class="dash-feature-description">
              Create and manage warming rooms. Keep your accounts active with automated conversations.
            </p>
            <a href="#" class="dash-feature-action" @click.prevent>
              View Rooms <i class="bi bi-arrow-right"></i>
            </a>
          </div>
        </div>

        <!-- Scripts Card -->
        <div class="dash-feature-card" @click="$router.push('/warming/scripts')">
          <div class="dash-feature-header">
            <h3 class="dash-feature-title">Scripts</h3>
            <div class="dash-feature-icon">
              <i class="bi bi-file-text"></i>
            </div>
          </div>
          <div class="dash-feature-content">
            <p class="dash-feature-description">
              Build conversation scripts with templates and dynamic variables for natural interactions.
            </p>
            <a href="#" class="dash-feature-action" @click.prevent>
              Create Script <i class="bi bi-arrow-right"></i>
            </a>
          </div>
        </div>

      </div>

      <!-- Quick Actions Card -->
      <div class="dash-actions-card">
        <div class="dash-actions-content">
          <h2 class="dash-actions-title">Quick Actions</h2>
          <p class="dash-actions-subtitle">Jump to the most important features</p>
          <div class="dash-actions-buttons">
            <button class="dash-action-btn" @click="$router.push('/instances')">
              <i class="bi bi-plus-circle-fill"></i>
              <span>Create Instance</span>
            </button>
            <button class="dash-action-btn" @click="$router.push('/warming/rooms')">
              <i class="bi bi-door-open-fill"></i>
              <span>New Room</span>
            </button>
            <button class="dash-action-btn" @click="$router.push('/warming/scripts')">
              <i class="bi bi-file-text-fill"></i>
              <span>New Script</span>
            </button>
            <button class="dash-action-btn" @click="$router.push('/warming/templates')">
              <i class="bi bi-code-square"></i>
              <span>New Template</span>
            </button>
          </div>
        </div>
        <div class="dash-illustration dash-rocket">
          <i class="bi bi-rocket-takeoff-fill"></i>
        </div>
      </div>

    </section>
  </main>
</template>

<script>
import axios from 'axios';

export default {
  data() {
    return {
      baseUrl: process.env.VUE_APP_BE_APP_BASE_URL,
      
      // Instances
      totalInstances: 0,
      onlineInstances: 0,
      
      // Warming
      totalRooms: 0,
      activeRooms: 0,
      totalScripts: 0,
      totalTemplates: 0,
      messagesToday: 0,
      successRate: 0
    }
  },
  
  mounted() {
    this.loadDashboardData();
  },
  
  methods: {
    async loadDashboardData() {
      await Promise.all([
        this.loadInstances(),
        this.loadRooms(),
        this.loadScripts(),
        this.loadTemplates(),
        this.loadTodayMessages()
      ]);
    },
    
    async loadInstances() {
      try {
        const response = await axios.get(`${this.baseUrl}/api/instances?all=true`);
        const instances = response.data.data.instances || [];
        
        this.totalInstances = instances.length;
        this.onlineInstances = instances.filter(i => i.status === 'online').length;
      } catch (error) {
        console.error('Failed to load instances:', error);
      }
    },
    
    async loadRooms() {
      try {
        const response = await axios.get(`${this.baseUrl}/api/warming/rooms`);
        const rooms = response.data.data.rooms || [];
        
        this.totalRooms = rooms.length;
        this.activeRooms = rooms.filter(r => r.status === 'ACTIVE').length;
      } catch (error) {
        console.error('Failed to load rooms:', error);
      }
    },
    
    async loadScripts() {
      try {
        const response = await axios.get(`${this.baseUrl}/api/warming/scripts`);
        const scripts = response.data.data.scripts || [];
        this.totalScripts = scripts.length;
      } catch (error) {
        console.error('Failed to load scripts:', error);
      }
    },
    
    async loadTemplates() {
      try {
        const response = await axios.get(`${this.baseUrl}/api/warming/templates`);
        const templates = response.data.data.templates || [];
        this.totalTemplates = templates.length;
      } catch (error) {
        console.error('Failed to load templates:', error);
      }
    },
    
    async loadTodayMessages() {
      try {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const response = await axios.get(`${this.baseUrl}/api/warming/logs`);
        const logs = response.data.data.logs || [];
        
        const todayLogs = logs.filter(log => {
          const logDate = new Date(log.executedAt || log.sentAt);
          return logDate >= today;
        });
        
        this.messagesToday = todayLogs.length;
        
        if (todayLogs.length > 0) {
          const successCount = todayLogs.filter(log => log.status === 'SUCCESS').length;
          this.successRate = Math.round((successCount / todayLogs.length) * 100);
        }
      } catch (error) {
        console.error('Failed to load today messages:', error);
      }
    }
  }
}
</script>

<style scoped>
@import '../../public/assets/css/dashboard-modern.css';
</style>
